import { useContext, useState, useEffect } from "react";
import { gql, useMutation } from "@apollo/client";
import matchSorter from "match-sorter";
import Moment from "moment";
import { AnimatePresence, motion } from "framer-motion";

import Button from "../toolBox/Button";
import Input from "../toolBox/Input";
// @ts-ignore
import InputField from "../toolBox/InputField";
// @ts-ignore
import styles from "./Forecast.module.css";
// @ts-ignore
import { colors } from "../../helpers/styles";

import { GET_DATASET_META } from "./Dataset";
// @ts-ignore
import { UserContext } from "../App";
import Check from "../toolBox/Check";
import Search from "../toolBox/Search";
import EmptyState from "../toolBox/EmptyState";
import { ChartScatterIcon } from "@hugeicons/react";
import DatePickerLeave from "../leave/components/DatePickerUpdated.tsx";
// @ts-ignore
import Modal from "../toolBox/Modal";
import Description from "../toolBox/Description";

type Adjustment = {
  id: string;
  name: string;
  description: string;
  startTime: string;
  endTime: string;
  type: "increase" | "decrease";
  amount: number;
  amountType: "absolute" | "percentage";
  metadata: {
    createdBy: {
      name: string;
      email: string;
    };
    updatedBy?: {
      name: string;
      email: string;
    };
  };
  createdAt: string;
  updatedAt: string;
  granularity: string;
};

function convertDateTimeFromUserTimezoneToDatasetTimezone(
  date: string,
  userTimezone: string,
  datasetTimezone: string
) {
  // First, parse the date string and set it to the user's timezone
  const userDate = Moment.tz(date, userTimezone);

  // Then convert it to the dataset timezone while preserving the local time
  const datasetDate = userDate.clone().tz(datasetTimezone);

  return datasetDate.toISOString();
}

export default function Adjustments(props: {
  id: string;
  handleClose: () => void;
  adjustments: Adjustment[];
  datasetTimezone: string;
  refetchVariables: {
    id: string;
    startDate: string;
    endDate: string;
    comparisonStartDate: string;
    comparisonEndDate: string;
  };
  handleRefetchData: () => void;
}) {
  const currentUser = useContext(UserContext);
  const [view, setView] = useState("list");

  const datasetTimezone = props.datasetTimezone;

  function handleClose() {
    props.handleClose();
  }

  let title = "Forecast adjustments";
  if (view === "add") {
    title = "Add adjustment";
  } else if (view.startsWith("detail-")) {
    title = "Edit adjustment";
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          style={{
            width: 375,
            position: "relative",
            background: "white",
            borderLeft: "1px solid #E0E0E0",
            boxSizing: "border-box",
          }}
          initial={{ right: -70 }}
          animate={{ right: 0 }}
          exit={{ right: -70 }}
          transition={{
            duration: 0.5,
            ease: [0, 1, 0.5, 1],
          }}
        >
          <div className={styles["staffing-parameters-header"]}>
            {title}
            <span className="bi_interface-cross" onClick={handleClose} />
          </div>
          {view.startsWith("list") && (
            <List
              adjustments={props.adjustments}
              isDeleting={view.split("-")[1]}
              handleSetView={setView}
              granularity={props.granularity}
            />
          )}
          {view === "add" && (
            <AddAdjustment
              timeFormat={currentUser.timeFormat}
              timezone={currentUser.timezone}
              datasetTimezone={datasetTimezone}
              handleSetView={setView}
              refetchVariables={props.refetchVariables}
              handleRefetchData={props.handleRefetchData}
            />
          )}
          {view.startsWith("detail-") && (
            <Detail
              currentUser={currentUser as any}
              datasetTimezone={datasetTimezone}
              adjustment={
                props.adjustments.find(
                  (adjustment) => adjustment.id === view.split("-")[1]
                )!
              }
              timeFormat={(currentUser as any).timeFormat}
              handleSetView={setView}
              refetchVariables={props.refetchVariables}
              handleRefetchData={props.handleRefetchData}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </>
  );
}

function List(props: {
  adjustments: Adjustment[];
  isDeleting: string | null;
  handleSetView: (view: string) => void;
  granularity: string;
}) {
  const [input, setInput] = useState("");

  const searchedAdjustments = matchSorter(props.adjustments, input, {
    keys: ["name"],
  });

  let granularity = props.granularity;
  if (props.granularity === "custom") {
    granularity = "period";
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div
        style={{
          padding: "0px 16px",
          borderBottom: "1px solid #E0E0E0",
          boxSizing: "border-box",
        }}
      >
        <Search
          value={input}
          name="input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Search adjustments (${props.adjustments.length})...`}
          margin="12px 0px 12px 0px"
          width="100%"
          customStyle={{ width: "100%" }}
        />
      </div>
      {searchedAdjustments.length === 0 ? (
        <div style={{ padding: "12px 16px" }}>
          <EmptyState
            title={
              props.adjustments.length === 0
                ? "No adjustments"
                : "No results found"
            }
            component={<ChartScatterIcon />}
            message={
              props.adjustments.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 24,
                    alignContent: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div>
                    No adjustments have been made for this {granularity}. Add
                    manual adjustments to improve the forecast.
                  </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      name="Add Adjustment"
                      icon="bi_interface-plus"
                      theme="blue-border"
                      action={() => props.handleSetView("add")}
                      customStyle={{
                        width: "fit-content",
                      }}
                    />
                  </div>
                </div>
              ) : (
                "No results found"
              )
            }
            customStyle={{
              border: 0,
            }}
          />
        </div>
      ) : (
        <>
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #E0E0E0",
              boxSizing: "border-box",
            }}
          >
            <Button
              icon="bi_interface-plus"
              name="Add Adjustment"
              theme="blue-border"
              action={() => {
                props.handleSetView("add");
              }}
              customStyle={{ width: "100%" }}
            />
          </div>
          <div className={styles["outliers-list"]}>
            {searchedAdjustments.map((adjustment: Adjustment) => {
              let dateLine = null;
              const endTimeIsZeroZero =
                Moment(adjustment.endTime).format("HH:mm") === "00:00";
              const endTime = endTimeIsZeroZero
                ? Moment(adjustment.endTime).subtract(1, "second")
                : adjustment.endTime;
              const isMultiDay = !Moment(adjustment.startTime).isSame(
                endTime,
                "day"
              );

              const isAllDay =
                Moment(adjustment.startTime).format("HH:mm") === "00:00" &&
                Moment(adjustment.endTime).format("HH:mm") === "00:00";

              if (isAllDay) {
                if (isMultiDay) {
                  dateLine = (
                    <span>
                      {Moment(adjustment.startTime).format("ddd, D MMM")} –{" "}
                      {Moment(endTime).format("ddd, D MMM")}
                    </span>
                  );
                } else {
                  dateLine = (
                    <span>
                      {Moment(adjustment.startTime).format("ddd, D MMM")}{" "}
                      <Divider /> All Day
                    </span>
                  );
                }
              } else {
                if (isMultiDay) {
                  dateLine = (
                    <span>
                      {Moment(adjustment.startTime).format("ddd, D MMM, HH:mm")}{" "}
                      – {Moment(endTime).format("ddd, D MMM, HH:mm")}
                    </span>
                  );
                } else {
                  dateLine = (
                    <span>
                      {Moment(adjustment.startTime).format("ddd, D MMM")}{" "}
                      <Divider /> {Moment(adjustment.startTime).format("HH:mm")}{" "}
                      – {Moment(endTime).format("HH:mm")}
                    </span>
                  );
                }
              }

              return (
                <div
                  key={adjustment.id}
                  onClick={() => props.handleSetView("detail-" + adjustment.id)}
                  className={styles["outlier-item"]}
                  style={{
                    opacity: props.isDeleting === adjustment.id ? 0.5 : 1,
                    cursor:
                      props.isDeleting === adjustment.id
                        ? "not-allowed"
                        : "pointer",
                    pointerEvents:
                      props.isDeleting === adjustment.id ? "none" : "auto",
                  }}
                >
                  <span
                    className="bi_interface-right"
                    style={{ position: "absolute", right: 6, top: 12 }}
                  />
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: "17px",
                      height: 17,
                    }}
                  >
                    {adjustment.name}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 300,
                      lineHeight: "17px",
                      height: 17,
                    }}
                  >
                    {dateLine}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Divider() {
  return <span style={{ color: "#e2e2e2" }}> | </span>;
}

function AddAdjustment(props: {
  timeFormat: string;
  timezone: string;
  datasetTimezone: string;
  handleSetView: (view: string) => void;
  refetchVariables: {
    id: string;
    startDate: string;
    endDate: string;
    comparisonStartDate: string;
    comparisonEndDate: string;
  };
  handleRefetchData: () => void;
}) {
  const [createAdjustment, { loading }] = useMutation(CREATE_ADJUSTMENT, {
    refetchQueries: [
      {
        query: GET_DATASET_META,
        variables: props.refetchVariables,
      },
    ],
    awaitRefetchQueries: true,
  });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [allDay, setAllDay] = useState(true);
  const [startDate, setStartDate] = useState(
    Moment().startOf("day").toISOString()
  );
  const [endDate, setEndDate] = useState(
    Moment().add(1, "day").startOf("day").toISOString()
  );
  const [adjustment, setAdjustment] = useState<{
    type: "increase" | "decrease";
    amount: number;
    amountType: "absolute" | "percentage";
  }>({
    type: "increase",
    amount: 0,
    amountType: "percentage",
  });

  const timezone = props.timezone;

  async function handleCreate() {
    let startTime = startDate;
    let endTime = endDate;
    if (allDay) {
      startTime = Moment(startDate).startOf("day").toISOString();
      endTime = Moment(endDate).startOf("day").toISOString();
      if (Moment(startDate).isSame(endDate, "day")) {
        endTime = Moment(endDate).add(1, "day").startOf("day").toISOString();
      }
    }

    const amountType =
      adjustment.amountType === "percentage" ? "percentage" : "absolute";
    const type = adjustment.type;

    const startTimeInDatasetTimezone =
      convertDateTimeFromUserTimezoneToDatasetTimezone(
        startTime,
        props.timezone,
        props.datasetTimezone
      );

    const endTimeInDatasetTimezone =
      convertDateTimeFromUserTimezoneToDatasetTimezone(
        endTime,
        props.timezone,
        props.datasetTimezone
      );

    await createAdjustment({
      variables: {
        name,
        description,
        startTime: startTimeInDatasetTimezone,
        endTime: endTimeInDatasetTimezone,
        amount: adjustment.amount,
        amountType,
        type,
        datasetId: props.refetchVariables.id,
      },
    });
    props.handleSetView("list");
    props.handleRefetchData();
  }

  let hasErrors = false;
  if (!name || name.length > 60) {
    hasErrors = true;
  }
  if (!startDate || !endDate) {
    hasErrors = true;
  }
  if (Moment(startDate).isSameOrAfter(Moment(endDate))) {
    hasErrors = true;
  }
  if (!adjustment.amount || adjustment.amount <= 0) {
    hasErrors = true;
  }
  const isDisabled = hasErrors;

  return (
    <>
      <div className={styles["staffing-parameters-body"]}>
        <div>
          <Input
            id="adjustment-name"
            name="adjustmentName"
            label="Name"
            value={name}
            placeholder="Enter adjustment name"
            handleChange={(e) => {
              setName(e.target.value);
            }}
            maxLength={60}
          />
        </div>
        <div>
          <div
            style={{
              width: "fit-content",
              display: "flex",
              gap: 12,
              fontSize: 14,
              fontWeight: 300,
            }}
            className={styles["model-item-checkbox"]}
            onClick={() => setAllDay(!allDay)}
          >
            <Check
              checked={allDay}
              handleClick={() => {
                setAllDay(!allDay);
              }}
            />
            <div>All day</div>
          </div>
        </div>

        <DatePickerLeave
          allDay={allDay}
          startDate={Moment(startDate)}
          endDate={Moment(endDate)}
          setDate={(date: string, type: string) => {
            if (type === "startDate") {
              setStartDate(Moment(date).toISOString());
            } else {
              setEndDate(Moment(date).toISOString());
            }
          }}
          timezone={timezone}
          format12h={props.timeFormat === "12-hour"}
        />
        <div style={{ marginTop: -24 }}>
          <AdjustmentAmount
            adjustment={adjustment}
            onAdjustmentChange={setAdjustment}
          />
        </div>
        <Description
          label="Add description (optional)"
          customStyle={{ width: "calc(100% + 1px)", resize: "none" }}
          rows={5}
          input={description}
          handleChange={(e) => setDescription(e.target.value)}
          metaText="This space can be used to provide additional information about this adjustment."
        />
      </div>
      <Footer
        action={{
          name: "Add",
          handle: handleCreate,
          loading,
        }}
        handleClose={() => {
          props.handleSetView("list");
        }}
        isDisabled={isDisabled}
        loading={loading}
      />
    </>
  );
}

function isAllDay(startTime: string, endTime: string) {
  return (
    Moment(startTime).format("HH:mm") === "00:00" &&
    Moment(endTime).format("HH:mm") === "00:00"
  );
}

function Detail(props: {
  adjustment: Adjustment;
  timeFormat: string;
  datasetTimezone: string;
  handleSetView: (view: string) => void;
  currentUser: {
    name: string;
    email: string;
    timezone: string;
  };
  refetchVariables: {
    id: string;
    startDate: string;
    endDate: string;
    comparisonStartDate: string;
    comparisonEndDate: string;
  };
  handleRefetchData: () => void;
}) {
  const [updateAdjustment, { loading: updateLoading }] = useMutation(
    UPDATE_ADJUSTMENT,
    {
      refetchQueries: [
        {
          query: GET_DATASET_META,
          variables: props.refetchVariables,
        },
      ],
      awaitRefetchQueries: true,
    }
  );
  const [deleteAdjustment, { loading: deleteLoading }] = useMutation(
    DELETE_ADJUSTMENT,
    {
      refetchQueries: [
        {
          query: GET_DATASET_META,
          variables: props.refetchVariables,
        },
      ],
      awaitRefetchQueries: true,
    }
  );
  const [name, setName] = useState(props.adjustment.name);
  const [allDay, setAllDay] = useState(
    isAllDay(props.adjustment.startTime, props.adjustment.endTime)
  );

  const [startDate, setStartDate] = useState(
    props.adjustment.startTime || Moment().startOf("day").toISOString()
  );
  const [endDate, setEndDate] = useState(
    props.adjustment.endTime ||
      Moment().add(1, "day").startOf("day").toISOString()
  );
  const [description, setDescription] = useState(props.adjustment.description);
  const [adjustment, setAdjustment] = useState<{
    type: "increase" | "decrease";
    amount: number;
    amountType: "absolute" | "percentage";
  }>({
    type: props.adjustment.type,
    amount: props.adjustment.amount,
    amountType: props.adjustment.amountType,
  });
  const [deleteModal, setDeleteModal] = useState(false);

  const timezone = props.currentUser.timezone;

  async function handleUpdate() {
    let data: {
      id: string;
      name?: string;
      startTime?: string;
      endTime?: string;
      description?: string;
      amount?: number;
      amountType?: string;
      type?: string;
      metadata?: {
        createdBy: {
          name: string;
          email: string;
        };
        updatedBy?: {
          name: string;
          email: string;
        };
      };
    } = {
      id: props.adjustment.id,
    };
    if (name !== props.adjustment.name) {
      data.name = name;
    }
    if (startDate !== props.adjustment.startTime) {
      data.startTime = startDate;
    }
    if (endDate !== props.adjustment.endTime) {
      data.endTime = endDate;
    }
    if (description !== props.adjustment.description) {
      data.description = description;
    }
    if (adjustment.amount !== props.adjustment.amount) {
      data.amount = adjustment.amount;
    }
    if (adjustment.amountType !== props.adjustment.amountType) {
      data.amountType = adjustment.amountType;
    }
    if (adjustment.type !== props.adjustment.type) {
      data.type = adjustment.type;
    }

    if (allDay) {
      data.startTime = Moment(startDate).startOf("day").toISOString();
      data.endTime = Moment(endDate).startOf("day").toISOString();
      if (Moment(startDate).isSame(endDate, "day")) {
        data.endTime = Moment(endDate)
          .add(1, "day")
          .startOf("day")
          .toISOString();
      }
    }

    if (data.startTime) {
      data.startTime = convertDateTimeFromUserTimezoneToDatasetTimezone(
        data.startTime,
        props.currentUser.timezone,
        props.datasetTimezone
      );
    }

    if (data.endTime) {
      data.endTime = convertDateTimeFromUserTimezoneToDatasetTimezone(
        data.endTime,
        props.currentUser.timezone,
        props.datasetTimezone
      );
    }

    await updateAdjustment({
      variables: {
        id: props.adjustment.id,
        name,
        startTime: data.startTime ? data.startTime : props.adjustment.startTime,
        endTime: data.endTime ? data.endTime : props.adjustment.endTime,
        description,
        amount: data.amount ? data.amount : props.adjustment.amount,
        amountType: data.amountType
          ? data.amountType
          : props.adjustment.amountType,
        type: data.type ? data.type : props.adjustment.type,
        metadata: {
          ...props.adjustment.metadata,
          updatedBy: {
            name: props.currentUser.name,
            email: props.currentUser.email,
          },
        },
        datasetId: props.refetchVariables.id,
      },
    });
    props.handleRefetchData();
  }

  async function handleDelete() {
    props.handleSetView(`list-${props.adjustment.id}`);
    await deleteAdjustment({
      variables: {
        id: props.adjustment.id,
        datasetId: props.refetchVariables.id,
      },
    });
    props.handleRefetchData();
  }

  const hasChanges =
    name !== props.adjustment.name ||
    startDate !== props.adjustment.startTime ||
    endDate !== props.adjustment.endTime ||
    description !== props.adjustment.description ||
    adjustment.amount !== props.adjustment.amount ||
    adjustment.amountType !== props.adjustment.amountType ||
    adjustment.type !== props.adjustment.type;
  let hasErrors = false;
  if (!name || name.length > 50) {
    hasErrors = true;
  }
  if (!startDate || !endDate) {
    hasErrors = true;
  }
  if (Moment(startDate).isSameOrAfter(Moment(endDate))) {
    hasErrors = true;
  }
  if (!adjustment.amount || adjustment.amount <= 0) {
    hasErrors = true;
  }
  const isDisabled = hasErrors;

  return (
    <>
      <div className={styles["staffing-parameters-body"]}>
        <div>
          <Input
            id="adjustment-name-edit"
            name="adjustmentNameEdit"
            label="Name"
            value={name}
            placeholder="Enter adjustment name"
            handleChange={(e) => {
              setName(e.target.value);
            }}
          />
        </div>
        <div>
          <div
            style={{
              width: "fit-content",
              display: "flex",
              gap: 12,
              fontSize: 14,
              fontWeight: 300,
            }}
            className={styles["model-item-checkbox"]}
            onClick={() => setAllDay(!allDay)}
          >
            <Check
              checked={allDay}
              handleClick={() => {
                setAllDay(!allDay);
              }}
            />
            <div>All day</div>
          </div>
        </div>

        <DatePickerLeave
          allDay={allDay}
          startDate={Moment(startDate)}
          endDate={Moment(endDate)}
          setDate={(date: string, type: string) => {
            if (type === "startDate") {
              setStartDate(Moment(date).toISOString());
            } else {
              setEndDate(Moment(date).toISOString());
            }
          }}
          timezone={timezone}
          format12h={props.timeFormat === "12-hour"}
          ignoreConvertTimezone={true}
        />
        <div style={{ marginTop: -24 }}>
          <AdjustmentAmount
            adjustment={adjustment}
            onAdjustmentChange={setAdjustment}
          />
        </div>
        <Description
          label="Add description (optional)"
          customStyle={{ width: "calc(100% + 1px)", resize: "none" }}
          rows={5}
          input={description}
          handleChange={(e) => setDescription(e.target.value)}
          metaText="This space can be used to provide additional information about this adjustment."
        />

        {props.adjustment.metadata.createdBy && props.adjustment.createdAt && (
          <div
            style={{
              fontSize: 14,
              fontWeight: 300,
              lineHeight: "17px",
              color: "rgba(0,0,0,0.87",
            }}
          >
            Created by {props.adjustment.metadata.createdBy.name} (
            {props.adjustment.metadata.createdBy.email}) on{" "}
            {Moment(props.adjustment.createdAt).format(
              `ddd, D MMM, ${props.timeFormat === "12-hour" ? "h:mma" : "HH:mm"}`
            )}
          </div>
        )}
        {props.adjustment.metadata.updatedBy && props.adjustment.updatedAt && (
          <div
            style={{
              fontSize: 14,
              fontWeight: 300,
              lineHeight: "17px",
              color: "rgba(0,0,0,0.87",
            }}
          >
            Last edited by {props.adjustment.metadata.updatedBy.name} (
            {props.adjustment.metadata.updatedBy.email}) on{" "}
            {Moment(props.adjustment.updatedAt).format(
              `ddd, D MMM, ${props.timeFormat === "12-hour" ? "h:mma" : "HH:mm"}`
            )}
          </div>
        )}
      </div>
      <Footer
        action={{
          name: hasChanges ? "Update" : "Delete",
          handle: hasChanges ? handleUpdate : () => setDeleteModal(true),
          color: hasChanges ? "blue" : "red-border",
          loading: updateLoading || deleteLoading,
        }}
        handleClose={() => {
          props.handleSetView("list");
        }}
        isDisabled={isDisabled}
        loading={updateLoading || deleteLoading}
      />
      {deleteModal && (
        <Modal
          title="Delete adjustment"
          color={colors.red}
          body={
            <p>
              Are you sure you want to delete this adjustment?
              <br />
              <strong className="underline">You can't undo this action.</strong>
            </p>
          }
          customStyle={{
            position: "fixed",
          }}
          handleFocus={() => {
            setDeleteModal(false);
          }}
          buttonOneName="Cancel"
          buttonTwoName="Delete"
          buttonOneTheme="grey-border"
          buttonTwoTheme="red-border"
          buttonOneAction={() => setDeleteModal(false)}
          buttonTwoAction={handleDelete}
          loading={deleteLoading}
        />
      )}
    </>
  );
}

function AdjustmentAmount(props: {
  adjustment: {
    type: "increase" | "decrease";
    amount: number;
    amountType: "absolute" | "percentage";
  };
  onAdjustmentChange: (adjustment: {
    type: "increase" | "decrease";
    amount: number;
    amountType: "absolute" | "percentage";
  }) => void;
}) {
  const [adjustmentType, setAdjustmentType] = useState<"increase" | "decrease">(
    props.adjustment.type
  );
  const [adjustmentValue, setAdjustmentValue] = useState<string>(
    props.adjustment.amount.toString()
  );
  const [adjustmentValueType, setAdjustmentValueType] = useState<
    "absolute" | "percentage"
  >(props.adjustment.amountType);

  const handleAdjustmentChange = () => {
    const value = parseFloat(adjustmentValue) || 0;
    props.onAdjustmentChange({
      type: adjustmentType,
      amount: value,
      amountType: adjustmentValueType,
    });
  };

  // Call handleAdjustmentChange whenever any value changes
  useEffect(() => {
    if (adjustmentValue) {
      handleAdjustmentChange();
    }
  }, [adjustmentType, adjustmentValue, adjustmentValueType]);

  const typeOptions = [
    {
      id: "increase",
      name: "Increase",
      action: () => setAdjustmentType("increase"),
    },
    {
      id: "decrease",
      name: "Decrease",
      action: () => setAdjustmentType("decrease"),
    },
  ];

  const valueTypeOptions = [
    {
      id: "percentage",
      name: "Percentage",
      action: () => setAdjustmentValueType("percentage"),
    },
    {
      id: "absolute",
      name: "Absolute",
      action: () => setAdjustmentValueType("absolute"),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 16 }}>
        <div>
          <InputField
            label="Adjustment type"
            value={adjustmentType === "increase" ? "Increase" : "Decrease"}
            changeInput={() => {}}
            options={typeOptions}
            fieldName="adjustmentType"
          />
        </div>
        <div>
          <InputField
            label="Adjustment value type"
            value={
              adjustmentValueType === "percentage" ? "Percentage" : "Absolute"
            }
            changeInput={() => {}}
            options={valueTypeOptions}
            fieldName="adjustmentValueType"
          />
        </div>
      </div>

      <div>
        <Input
          id="adjustment-value"
          name="adjustmentValue"
          label="Adjustment amount"
          value={adjustmentValue}
          type="number"
          placeholder="Enter amount"
          handleChange={(e) => setAdjustmentValue(e.target.value)}
          patternSignifier={
            adjustmentValueType === "percentage" ? "%" : undefined
          }
        />
      </div>
    </div>
  );
}

function Footer(props: {
  handleClose: () => void;
  action: {
    name: string;
    handle: () => void;
    color?: string;
    loading?: boolean;
  };
  isDisabled: boolean;
  loading: boolean;
}) {
  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        width: "100%",
        height: 70,
        borderTop: "1px solid #E0E0E0",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 16px",
        background: "white",
      }}
    >
      <Button
        name="Cancel"
        theme="grey-border"
        size="large"
        action={props.handleClose}
      />
      <Button
        name={props.action.name}
        theme={props.action.color ? props.action.color : "blue"}
        size="large"
        disabled={props.isDisabled}
        action={props.action.handle}
        loading={props.action.loading}
      />
    </div>
  );
}

const CREATE_ADJUSTMENT = gql`
  mutation createAdjustment(
    $name: String!
    $description: String
    $startTime: DateTime!
    $endTime: DateTime!
    $amount: Float!
    $amountType: String!
    $type: String!
    $datasetId: ID!
  ) {
    createAdjustment(
      name: $name
      description: $description
      startTime: $startTime
      endTime: $endTime
      amount: $amount
      amountType: $amountType
      type: $type
      datasetId: $datasetId
    ) {
      id
      name
      description
      startTime
      endTime
      amount
      amountType
      type
      metadata
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_ADJUSTMENT = gql`
  mutation updateOutlier(
    $id: ID!
    $name: String!
    $description: String
    $startTime: DateTime!
    $endTime: DateTime!
    $amount: Float
    $amountType: String
    $type: String
    $metadata: JSON
    $datasetId: ID!
  ) {
    updateAdjustment(
      id: $id
      name: $name
      description: $description
      startTime: $startTime
      endTime: $endTime
      amount: $amount
      amountType: $amountType
      type: $type
      metadata: $metadata
      datasetId: $datasetId
    ) {
      id
      name
      description
      startTime
      endTime
      amount
      amountType
      type
      metadata
      createdAt
      updatedAt
    }
  }
`;

const DELETE_ADJUSTMENT = gql`
  mutation deleteAdjustment($id: ID!, $datasetId: ID!) {
    deleteAdjustment(id: $id, datasetId: $datasetId) {
      id
    }
  }
`;
